/**
 * Service categories API wrapper (v2 — Project Independence, DNKADM-0052 U1).
 *
 * Service categories are a NEW v2 entity (handoff §5.3): a purely
 * grouping/labeling taxonomy on services — **decoupled from credits** (the v1
 * "category-name doubles as the credit bucket" conflation is gone; buckets are
 * their own entity, §5.4 / `creditBuckets.ts`). A service carries 0..n categories
 * (many-to-many, managed from the service form, 0055); NO credit logic keys off a
 * category. This module owns the CRUD + soft-delete/reactivate (§4.3) and the
 * fail-closed category pick-list the service form consumes.
 *
 * Bearer-only `/v2/admin/**` surface — `request()` routes it (DNKADM-0049
 * `isV2Surface`): bearer token, NO CSRF, NO cookies, the bounded
 * 401→refresh→retry; the mutating-audit `X-Session-Mode` stamp fires on the writes
 * (`isV2AdminSurface`).
 *
 * **Endpoints (handoff §5.3, §3.1 — standard REST + soft-delete):**
 *  - `GET    /v2/admin/service-categories?status=active|inactive|all` → {@link ServiceCategory}`[]`.
 *  - `POST   /v2/admin/service-categories` ← {@link ServiceCategoryInput} → the created row.
 *  - `PUT    /v2/admin/service-categories/{id}` ← {@link ServiceCategoryInput} → the updated row.
 *  - `POST   /v2/admin/service-categories/{id}/deactivate` | `/reactivate` (§4.3) → the updated row.
 *  - `POST   /v2/admin/service-categories/{id}/publish` (DNKAPI-0261) → the updated row (Draft → live).
 *
 * **`v2_service_category` field set (handoff §5.3):** `id`, `name`, `description`
 * (optional), `isActive`. Unique on `(tenant, name)` — the create/update `409`
 * surfaces inline. Every field is parsed DEFENSIVELY (mirrors `locations.ts`): a
 * missing/wrong-typed field degrades (an entry with no usable `id` is dropped),
 * never a throw.
 */
import { request } from './client';
import { PickListError } from './picklists';
import type { ActiveFilterValue } from '@/hooks/useActiveFilter';

/** v2 admin service-categories base path (bearer surface). */
const PATH = '/v2/admin/service-categories';

/**
 * One `v2_service_category` row — a grouping/labeling tag on services, decoupled
 * from credits (§5.3). `description` is an optional string (absent when the server
 * carries none — `exactOptionalPropertyTypes`: the key is omitted, never
 * `undefined`).
 */
/** The three lifecycle states a category can be in (DNKAPI-0261). */
type ServiceCategoryStatus = 'DRAFT' | 'ACTIVE' | 'INACTIVE';

export interface ServiceCategory {
  id: string;
  name: string;
  /** Soft-delete flag (§4.3); `false` = deactivated. */
  isActive: boolean;
  /**
   * Lifecycle status (DNKAPI-0261): `DRAFT` (created, never published), `ACTIVE`
   * (published and live), `INACTIVE` (deactivated/soft-removed). Independent of
   * `isActive`: a Draft is still `isActive:true` underneath; `INACTIVE` and
   * `isActive:false` move together once a category has been published. Always
   * present on a GET response.
   */
  status: ServiceCategoryStatus;
  description?: string;
  /**
   * Whether the public UI shows the category as a booking entry point
   * (DNKADM-0267). **Display-only** — it never gates booking, client- or
   * server-side. Assigned only when the wire carries a real boolean so the form
   * can omit-to-preserve anything else.
   */
  bookable?: boolean;
}

/**
 * Create/update body for a service category. `name` is required by the form;
 * `description` is optional. `isActive` is NOT settable here — activation is the
 * dedicated deactivate/reactivate endpoints (§4.3).
 */
export interface ServiceCategoryInput {
  name: string;
  description?: string;
  /**
   * Nullable **preserve-on-null** on the wire (DNKADM-0267): omitted when the form
   * has no value (create → server defaults `true`; update → preserves). Display-only.
   */
  bookable?: boolean;
}

/** Options for {@link listServiceCategories}. */
export interface ListServiceCategoriesOptions {
  /** Active/Inactive/All filter (§4.3); defaults to `active` (live rows only). */
  status?: ActiveFilterValue;
}

/**
 * The minimal `{ id, name }` shape {@link getServiceCategoryOptions} returns —
 * `ServiceDetail` passes that fetcher through `usePickList` for the service form (0055).
 */
export interface ServiceCategoryOption {
  id: string;
  name: string;
  isActive: boolean;
}

// --- Defensive parsing helpers (mirrors locations.ts) --------------------------

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** A trimmed non-empty string, or null. */
function asText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/**
 * A stable string id from a NUMBER or a string (DNKADM-0064): v2 entity ids arrive
 * as numbers on the wire and are normalized to the `string` the UI/router use — a
 * finite number → `String(n)`, a non-empty string → trimmed, else null. Preserves
 * genuinely-string ids (accepts both); a numeric id is never dropped.
 */
function asId(value: unknown): string | null {
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
  return asText(value);
}

/**
 * A boolean from a real boolean, or a fallback when absent/unparseable. `isActive`
 * defaults to `true` on read (a row the server returned without the flag is treated
 * as live — the list endpoint returns live rows by default, §4.3).
 */
function asBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

/** Parse a raw lifecycle status; anything unrecognized degrades to `ACTIVE` (never crashes). */
function asCategoryStatus(value: unknown): ServiceCategoryStatus {
  if (typeof value === 'string') {
    const upper = value.toUpperCase();
    if (upper === 'DRAFT') return 'DRAFT';
    if (upper === 'INACTIVE') return 'INACTIVE';
  }
  return 'ACTIVE';
}

/**
 * Parse one raw category, or null when it has no usable `id` (an entry with no
 * selectable id is dropped rather than surfaced as a half-identified option). A
 * missing `name` degrades to an empty string (the row still renders; the server is
 * the authority on the real value). `description` is assigned only when present
 * (exactOptionalPropertyTypes).
 */
function parseCategory(raw: unknown): ServiceCategory | null {
  if (!isObject(raw)) return null;
  const id = asId(raw['id']);
  if (id === null) return null;

  const category: ServiceCategory = {
    id,
    name: asText(raw['name']) ?? '',
    // The wire flag is `active` (DNKADM-0064); tolerate legacy `isActive`.
    isActive: asBoolean(raw['active'] ?? raw['isActive'], true),
    status: asCategoryStatus(raw['status']),
  };
  const description = asText(raw['description']);
  if (description !== null) category.description = description;
  // Display-only booking-entry-point flag (DNKADM-0267) — only a real boolean is
  // surfaced; anything else stays absent so the form omits (preserves) it.
  if (typeof raw['bookable'] === 'boolean') category.bookable = raw['bookable'];
  return category;
}

/**
 * Extract the categories array TOLERANTLY from a successful body, returning `null`
 * when NEITHER a bare array NOR a known wrapper key
 * (`serviceCategories`/`categories`/`content`) yields one. The list reads turn
 * `null` into `[]` (an empty list is a real first-run state, §8); the fail-closed
 * pick-list turns it into a {@link PickListError} (a non-array success cannot be
 * trusted as "complete and empty").
 */
function extractCategories(body: unknown): unknown[] | null {
  if (Array.isArray(body)) return body;
  if (isObject(body)) {
    if (Array.isArray(body['serviceCategories'])) return body['serviceCategories'];
    if (Array.isArray(body['categories'])) return body['categories'];
    if (Array.isArray(body['content'])) return body['content'];
  }
  return null;
}

/**
 * Build the JSON body for a create/update, omitting an absent `description`.
 * `bookable` is preserve-on-null (DNKADM-0267) — sent only with an actual value.
 */
function toBody(input: ServiceCategoryInput): Record<string, unknown> {
  const json: Record<string, unknown> = { name: input.name };
  if (input.description !== undefined) json['description'] = input.description;
  if (input.bookable !== undefined) json['bookable'] = input.bookable;
  return json;
}

// --- Endpoints -----------------------------------------------------------------

/**
 * `GET /v2/admin/service-categories?status=…` → the tenant's categories,
 * defensively parsed (a bad entry is dropped). The `status` filter (§4.3) defaults
 * to `active` (live rows only); pass `inactive`/`all` to include deactivated rows.
 * Throws a typed `ApiError` on a non-2xx response.
 */
export async function listServiceCategories(
  options: ListServiceCategoriesOptions = {},
): Promise<ServiceCategory[]> {
  const status = options.status ?? 'active';
  const body = await request<unknown>('GET', PATH, { query: { status } });
  return (extractCategories(body) ?? [])
    .map(parseCategory)
    .filter((c): c is ServiceCategory => c !== null);
}

/**
 * `POST /v2/admin/service-categories` → create a category. Returns the re-parsed
 * created row. Throws a typed `ApiError` (`400` VALIDATION inline-able, `409`
 * CONFLICT on the unique `(tenant, name)` rule, upstream).
 */
export async function createServiceCategory(
  input: ServiceCategoryInput,
): Promise<ServiceCategory | null> {
  const body = await request<unknown>('POST', PATH, { json: toBody(input) });
  return parseCategory(body);
}

/**
 * `PUT /v2/admin/service-categories/{id}` → update a category. Returns the
 * re-parsed updated row. Throws a typed `ApiError`.
 */
export async function updateServiceCategory(
  id: string,
  input: ServiceCategoryInput,
): Promise<ServiceCategory | null> {
  const body = await request<unknown>('PUT', `${PATH}/${encodeURIComponent(id)}`, {
    json: toBody(input),
  });
  return parseCategory(body);
}

/**
 * `POST /v2/admin/service-categories/{id}/deactivate` → soft-deactivate a category
 * (§4.3 — never a hard delete). Throws a typed `ApiError`.
 */
export async function deactivateServiceCategory(id: string): Promise<void> {
  await request<unknown>('POST', `${PATH}/${encodeURIComponent(id)}/deactivate`);
}

/**
 * `POST /v2/admin/service-categories/{id}/reactivate` → reactivate a
 * soft-deactivated category (§4.3). Throws a typed `ApiError`.
 */
export async function reactivateServiceCategory(id: string): Promise<void> {
  await request<unknown>('POST', `${PATH}/${encodeURIComponent(id)}/reactivate`);
}

/**
 * `POST /v2/admin/service-categories/{id}/publish` → take a Draft category live. No
 * body. Idempotent — publishing an already-published category is a no-op that returns
 * its current state. Returns the re-parsed updated row. Throws a typed `ApiError`.
 */
export async function publishServiceCategory(id: string): Promise<ServiceCategory | null> {
  const body = await request<unknown>('POST', `${PATH}/${encodeURIComponent(id)}/publish`);
  return parseCategory(body);
}

// --- The shared, fail-closed service-category pick-list -------------------------

/**
 * Load the active service categories as `{ id, name }` options, **fail-closed**:
 * any upstream/network failure OR a malformed (non-array) `200` body throws the
 * shared {@link PickListError}, so the loader renders error + Retry and NEVER a
 * silent empty list (a failure can never masquerade as "loaded, empty"). A
 * successful empty list returns `[]` (a real first-run state — the caller renders
 * "no categories"). Only `active` categories are offered (a deactivated category
 * must not be selectable on a service, 0055).
 *
 * Mirrors {@link getCreditBucketOptions} / the `@/api/picklists` fail-closed
 * wrappers, reusing their `PickListError` so `usePickList`'s `failClosed` detection
 * (and the loader copy) work unchanged.
 */
export async function getServiceCategoryOptions(): Promise<ServiceCategoryOption[]> {
  try {
    const body = await request<unknown>('GET', PATH, { query: { status: 'active' } });
    const list = extractCategories(body);
    // A non-array success cannot be trusted as a COMPLETE list — fail-closed
    // rather than present it as an empty pick-list.
    if (list === null) throw new Error('expected an array of service categories');
    return list
      .map(parseCategory)
      .filter((c): c is ServiceCategory => c !== null)
      // Endpoint stays `status: 'active'`, so `isActive` is always `true` today — it
      // exists only so `ServiceCategoryOption` keeps satisfying the shared
      // `IdNameOption` contract `AssignmentEditor` is generic over (DNKADM-0134).
      .map((c) => ({ id: c.id, name: c.name, isActive: c.isActive }));
  } catch (cause) {
    throw new PickListError('service-categories', cause);
  }
}
